import { Link, useSearchParams } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { WorkList } from "./components/WorkList";
import { Timeline } from "./components/Timeline";
import { LoadContext } from "./helpers/LoadContext";
import { ResetContext } from "./helpers/ResetContext";
import { DefaultItem, DefaultWorksList } from "./helpers/Constants";
import { SORT_FN_MAP } from "./helpers/sortWorks";
import { Dropdown } from "./components/Dropdown";
import backIconColor from "./assets/marbles/Back_icon_blue.mp4";
import backIconClick from "./assets/marbles/Back_icon_click.mp4";

function App() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [filterState, setFilter] = useState(
    searchParams.get("filter") ?? "none",
  );
  const [firstLoad, setFirstLoad] = useState(true);
  const [viewMode, setViewMode] = useState('curated');

  // set up work list
  const currSortRef = useRef("new-old");
  const worksRef = useRef(
    DefaultWorksList.sort(SORT_FN_MAP[currSortRef.current]),
  );
  const [dynamicWorksList, setList] = useState(
    Array.from({ length: worksRef.current.length }, () => DefaultItem),
  );

  // preload videos for Profile page
  useEffect(() => {
    // Fetch videos to cache them in browser
    fetch(backIconColor).catch(() => { });
    fetch(backIconClick).catch(() => { });
  }, []);

  // on load
  useEffect(() => {
    if (searchParams.size > 0 && searchParams.get("filter")) {
      setFilter(searchParams.get("filter"));
    }
  }, [searchParams]);

  // dynamic work list loading, runs on filter, sort, and viewMode
  useEffect(() => {
    worksRef.current = DefaultWorksList.filter(
      (w) =>
        (viewMode === 'full' || w.curated === true) &&
        (filterState === "none" || w.filter === filterState),
    );
    setList(Array.from({ length: worksRef.current.length }, () => DefaultItem));
    for (let i = 0; i < worksRef.current.length + 1; i++) {
      setTimeout(() => {
        setList((d) => [...worksRef.current.slice(0, i), ...d.slice(i)]);
      }, i * 35);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState, currSortRef.current, viewMode]);

  // reset context
  const [resetFlag, setResetFlag] = useState(false);

  // handlers
  const handleReset = () => {
    handleFilter();
    handleSort();
  };

  const toggleViewMode = () => {
    setViewMode((m) => {
      const next = m === 'curated' ? 'full' : 'curated';
      setFilter('none');
      setSearchParams({});
      if (next === 'full') handleSort('new-old');
      return next;
    });
  };

  const handleFilter = (newFilter) => {
    const alternate = !newFilter || filterState === newFilter;
    setFilter(alternate ? "none" : newFilter);
    setSearchParams(alternate ? {} : { filter: newFilter });
  };

  const handleSort = (newSort) => {
    const newSortFn = SORT_FN_MAP[newSort];
    setResetFlag((f) => !f);

    // reset case, default to new-old
    if (currSortRef.current === newSort || !newSortFn) {
      if (newSort === "new-old") return;
      worksRef.current = worksRef.current.sort(SORT_FN_MAP["new-old"]);
      currSortRef.current = "new-old";
      setList(worksRef.current);
      return;
    }

    worksRef.current = worksRef.current.sort(newSortFn);
    currSortRef.current = newSort;
    setList(worksRef.current);
  };

  const createFilterObj = (titles) => {
    return Array.from(titles, (title) => {
      return {
        title,
        key: title.slice(0, 3),
        handler: () => handleFilter(title),
        active: filterState === title,
      };
    });
  };

  const createSortObj = (titles) => {
    return Array.from(titles, (title) => {
      return {
        title,
        key: title.slice(0, 3),
        handler: () => handleSort(title),
        active: currSortRef.current === title,
      };
    });
  };

  return (
    <LoadContext.Provider value={{ setFirstLoad, firstLoad }}>
      <ResetContext.Provider value={resetFlag}>
        <div className="flex flex-col gap-16 p-4 pt-8 text-xs sm:text-base h-full bg-clouds-300 dark:bg-midnight-900 text-clouds-900 dark:text-midnight-50">
          <div className="max-w-5xl">
            <h2 className="font-light">
              <Link to={"/profile"} className="font-bold hover:text-accent-500 dark:hover:text-accent-600 bg-[linear-gradient(currentColor,currentColor)] bg-no-repeat [background-position:0_100%] [background-size:0%_1px] hover:[background-size:100%_1px] transition-[background-size] duration-300">
                LUCAS SANCHES FERREIRA{" "}
              </Link>
            </h2>
            <p>
              Editor and creative in commercial post-production.
            </p>
          </div>
          <div className="flex w-full justify-between items-end -mb-8">
            <Timeline
              reset={handleReset}
              viewMode={viewMode}
              toggleViewMode={toggleViewMode}
            />
            <div className="flex gap-2">
              <Dropdown
                text={"filter"}
                options={createFilterObj(["freelance", "commercial"])}
                disabled={viewMode === 'curated'}
              />
              <Dropdown
                text={"sort"}
                options={createSortObj(["new-old", "old-new", "a-z", "z-a"])}
                disabled={viewMode === 'curated'}
              />
            </div>
          </div>
          <WorkList works={dynamicWorksList} />
        </div>
      </ResetContext.Provider>
    </LoadContext.Provider>
  );
}

export default App;
